import { withAsyncContext as _withAsyncContext, defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-col md:flex-row h-screen w-full overflow-hidden bg-white dark:bg-gray-950" }
const _hoisted_2 = { class: "relative w-full md:w-1/2 h-48 md:h-full overflow-hidden bg-purple-50 dark:bg-gray-900" }
const _hoisted_3 = { class: "absolute top-6 left-6 z-20" }
const _hoisted_4 = { class: "w-full md:w-1/2 flex items-center justify-center p-8 md:p-12 overflow-y-auto" }
const _hoisted_5 = {
  key: 0,
  class: "text-center"
}
const _hoisted_6 = { class: "text-2xl font-bold" }
const _hoisted_7 = { class: "mt-2 text-gray-600 dark:text-gray-300" }
const _hoisted_8 = {
  key: 1,
  class: "w-full max-w-md space-y-8"
}

import * as z from "zod";
import type { FormSubmitEvent } from "#ui/types";
import { useAuthStore } from "~/stores/auth";

import { passwordRule, passwordMismatchErrorMessage } from "~/schemas/auth";

type Schema = z.output<typeof schema>;


export default /*@__PURE__*/_defineComponent({
  __name: 'signup',
  async setup(__props) {

let __temp: any, __restore: any

const toast = useToast();
const { $api } = useNuxtApp();

definePageMeta({
  layout: "auth",
});

useHead({
  title: "Signup to FamilyAxis",
  meta: [{ name: "description", content: "Signup to FamilyAxis" }],
  bodyAttrs: {
    class: "Signup to FamilyAxis",
  },
});

// const api = useRuntimeConfig().public.api;
// const route = useRoute();
const auth = useAuthStore();

const fields = [
  {
    name: "firstname",
    type: "text" as const,
    label: "First Name",
    placeholder: "Enter your first name",
    required: true,
  },
  {
    name: "surname",
    type: "text" as const,
    label: "Last Name",
    placeholder: "Enter your last name",
    required: true,
  },
  {
    name: "email",
    type: "text" as const,
    label: "Email",
    placeholder: "Enter your email",
    required: true,
  },
  {
    name: "password",
    label: "Password",
    type: "password" as const,
    placeholder: "Enter your password",
    required: true,
  },
  {
    name: "confirmPassword",
    label: "Password confirmation",
    type: "password" as const,
    placeholder: "Confirm your password",
    required: true,
  },
];

const isConnected = (
  ([__temp,__restore] = _withAsyncContext(() => auth.isAuthenticated())),
  __temp = await __temp,
  __restore(),
  __temp
);

const schema = z
  .object({
    firstname: z
      .string()
      .min(1, "First name is required")
      .max(20, "First name must be 20 characters or less"),
    surname: z
      .string()
      .min(1, "Last name is required")
      .max(20, "Last name must be 20 characters or less"),
    email: z.string().email("Invalid email"),
    password: passwordRule,
    confirmPassword: passwordRule,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: passwordMismatchErrorMessage,
    path: ["confirmPassword"],
  });

const errorsubmit = ref<string>();

// Watch for changes on errorsubmit
watch(errorsubmit, (newValue, oldValue) => {
  if (newValue) {
    setTimeout(() => {
      errorsubmit.value = "";
    }, 2000);
  }
});

const providers = [
  {
    label: "Google",
    onClick: () => {
      toast.add({ title: "Google", description: "Sign up with Google" });
      window.location.href = $api("/auth/google/redirect");
    },
    icon: "i-mdi:google",
    color: "neutral" as const, // Using a supported color
  },
];

async function onSubmit(payload: FormSubmitEvent<Schema>) {
  try {
    const formData = payload.data;

    // Prepare data for API call
    const registerData = {
      firstname: formData.firstname,
      surname: formData.surname,
      email: formData.email,
      password: formData.password,
      is_verified: false,
      suspended: false,
      thumbnail: "picturelink1234",
    };

    const result = (await auth.register(registerData)) as any;

    if (result.token) {
      navigateTo("/login");
      toast.add({
        title: "Registration successful",
        description: "Confirm your email address to complete your registration.",
        icon: "i-heroicons-envelope",
        color: "success",
        duration: 10000,
      });
    } else {
      throw new Error("Registration failed");
    }
  } catch (error) {
    errorsubmit.value = "Registration failed. Please try again or contact support.";
    console.error("Error:", error);
  }
}

return (_ctx: any,_cache: any) => {
  const _component_UButton = _resolveComponent("UButton")!
  const _component_UColorModeImage = _resolveComponent("UColorModeImage")!
  const _component_UAlert = _resolveComponent("UAlert")!
  const _component_NuxtLink = _resolveComponent("NuxtLink")!
  const _component_UAuthForm = _resolveComponent("UAuthForm")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_UButton, {
          to: "/",
          icon: "i-heroicons-arrow-left",
          variant: "ghost",
          color: "gray",
          class: "hover:bg-white/50 dark:hover:bg-black/50 transition-colors",
          "aria-label": "Back to home"
        })
      ]),
      _cache[1] || (_cache[1] = _createElementVNode("div", { class: "absolute inset-0" }, [
        _createElementVNode("div", { class: "wave wave-1" }),
        _createElementVNode("div", { class: "wave wave-2" }),
        _createElementVNode("div", { class: "wave wave-3" })
      ], -1)),
      _createElementVNode("div", {
        class: "absolute top-8 left-8 z-20 hidden md:flex items-center gap-3 hover:cursor-pointer",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.navigateTo('/')))
      }, [
        _createVNode(_component_UColorModeImage, {
          light: "/images/logo_light.svg",
          dark: "/images/logo_dark.svg",
          class: "h-10 w-auto",
          alt: "FamilyAxis Logo"
        }),
        _createVNode(_component_UColorModeImage, {
          light: "/images/familyaxis.svg",
          dark: "/images/familyaxis_dark.svg",
          class: "h-6 w-auto",
          alt: "FamilyAxis Text"
        })
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      (_unref(isConnected))
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("h1", _hoisted_6, "You are connected as " + _toDisplayString(_unref(auth).user?.email), 1),
            _createElementVNode("p", _hoisted_7, "Welcome, " + _toDisplayString(_unref(auth).user?.firstname), 1)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(_component_UAuthForm, {
              schema: _unref(schema),
              fields: fields,
              providers: providers,
              title: "Register a new account",
              icon: "i-heroicons-lock-closed",
              onSubmit: onSubmit
            }, {
              validation: _withCtx(() => [
                (_unref(errorsubmit))
                  ? (_openBlock(), _createBlock(_component_UAlert, {
                      key: 0,
                      color: "error",
                      icon: "i-heroicons-information-circle-20-solid",
                      title: "Error signing up",
                      description: _unref(errorsubmit)
                    }, null, 8, ["description"]))
                  : _createCommentVNode("", true)
              ]),
              footer: _withCtx(() => [
                _cache[3] || (_cache[3] = _createTextVNode(" By signing up, you agree to our ", -1)),
                _createVNode(_component_NuxtLink, {
                  to: "/",
                  class: "text-primary-500 font-medium hover:underline"
                }, {
                  default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                    _createTextVNode("Terms of Service", -1)
                  ]))]),
                  _: 1
                }),
                _cache[4] || (_cache[4] = _createTextVNode(". ", -1))
              ]),
              _: 1
            }, 8, ["schema"])
          ]))
    ])
  ]))
}
}

})